import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Globe, Menu, X, ChevronDown, ShoppingBag } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { NAV, LANGUAGES } from '../../data/content';
import { useQuote } from './QuoteContext';
import { useCart } from '../../context/CartContext';
import { useSiteData } from '../../context/SiteDataContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from 'react-i18next';

const MENU_ITEMS = [
  { label: 'nav.home', to: '/' },
  { label: 'nav.specialties', to: '/products' },
  { label: 'nav.quality', to: '/quality-and-manufacturing' },
  { label: 'nav.downloads', to: '/downloads' },
  { label: 'nav.faqs', to: '/faqs' },
  { label: 'nav.about_us', to: '/about-us' },
  { label: 'nav.contact', to: '/contact' },
];

const Header = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const { locale, setLocale } = useLanguage();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openQuote } = useQuote();
  const { count, setOpen: setCartOpen } = useCart();
  const { settings } = useSiteData();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const solid = scrolled || location.pathname !== '/';

  const goSection = (href) => {
    setMobileOpen(false);
    if (location.pathname === '/') {
      if (window.__lenis) window.__lenis.scrollTo(href, { offset: -80 });
      else document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/' + href);
    }
  };

  const hasPromo = settings.enable_promo_notice === true;

  return (
    <>
      {hasPromo && (
        <div className="fixed top-0 left-0 w-full bg-slate-900 text-white py-2 px-5 text-center text-xs font-semibold tracking-wide z-50 flex items-center justify-center gap-1.5 h-9">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0"></span>
          <a href={settings.promo_notice_url || '/quality-and-manufacturing'} className="hover:underline">
            {settings.promo_notice_text || 'Global Export Dispatch — ISO 13485 & CE MDR Compliant'}
          </a>
        </div>
      )}

      <motion.header
        data-testid="site-header"
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed left-0 z-40 w-full transition-all duration-500 ${
          solid
            ? 'bg-white/80 backdrop-blur-xl border-b border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)]'
            : 'bg-transparent'
        }`}
        style={{ top: (hasPromo && !scrolled) ? '36px' : '0px' }}
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 lg:px-10">
          <Link to="/" data-testid="logo-link" className="flex items-center">
            <img src={settings.site_logo || "/logo-color.png"} alt={settings.site_name || "MEDTREND"} className="h-7 w-auto md:h-8" />
          </Link>

          <nav className="hidden items-center gap-7 lg:flex">
            {MENU_ITEMS.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="relative font-display text-sm font-medium text-mt-ink/80 transition-colors hover:text-mt-blue after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:w-0 after:bg-mt-orange after:transition-all after:duration-300 hover:after:w-full"
              >
                {t(item.label)}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-1 sm:gap-2">
            <button
              data-testid="search-toggle"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className="flex h-10 w-10 items-center justify-center rounded-full text-mt-ink transition-colors hover:bg-mt-blue/10 hover:text-mt-blue"
            >
              <Search className="h-5 w-5" strokeWidth={1.5} />
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="language-selector"
                  className="hidden items-center gap-1 rounded-full px-3 py-2 font-display text-sm font-medium text-mt-ink transition-colors hover:bg-mt-blue/10 hover:text-mt-blue sm:flex"
                >
                  <Globe className="h-4 w-4" strokeWidth={1.5} />
                  {(locale || 'en').toUpperCase()}
                  <ChevronDown className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl">
                {LANGUAGES.map((l) => (
                  <DropdownMenuItem key={l.code} data-testid={`lang-${l.code}`} onClick={() => setLocale(l.code)} className="cursor-pointer">
                    <span className="w-8 font-semibold">{l.code.toUpperCase()}</span>
                    <span className="text-mt-muted">{l.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {settings.enable_b2c_checkout !== false && (
              <button
                data-testid="cart-button"
                onClick={() => setCartOpen(true)}
                aria-label="Cart"
                className="relative flex h-10 w-10 items-center justify-center rounded-full text-mt-ink transition-colors hover:bg-mt-blue/10 hover:text-mt-blue"
              >
                <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
                {count > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-mt-orange px-1 font-display text-[10px] font-bold text-white" data-testid="cart-count">
                    {count}
                  </span>
                )}
              </button>
            )}

            <button
              data-testid="header-get-quote"
              onClick={openQuote}
              className="ml-1 hidden rounded-full bg-mt-orange px-6 py-2.5 font-display text-sm font-semibold text-white shadow-[0_8px_24px_rgba(235,93,11,0.25)] transition-all duration-300 hover:bg-mt-orangeLight hover:-translate-y-0.5 sm:block"
            >
              Get Quote
            </button>

            <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(true)} aria-label="Menu" className="flex h-10 w-10 items-center justify-center rounded-full text-mt-ink transition-colors hover:bg-mt-blue/10 lg:hidden">
              <Menu className="h-6 w-6" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Search overlay */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] flex items-start justify-center bg-mt-footer/40 px-5 pt-28 backdrop-blur-sm" onClick={() => setSearchOpen(false)} data-testid="search-overlay">
            <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl rounded-[24px] bg-white p-3 shadow-2xl">
              <div className="flex items-center gap-3 px-4">
                <Search className="h-6 w-6 text-mt-blue" strokeWidth={1.5} />
                <input autoFocus data-testid="search-input" placeholder="Search instruments, categories, OEM services…" className="w-full bg-transparent py-4 text-lg text-mt-ink placeholder:text-mt-muted focus:outline-none" />
                <button onClick={() => setSearchOpen(false)} className="text-mt-muted hover:text-mt-ink"><X className="h-5 w-5" /></button>
              </div>
              <div className="flex flex-wrap gap-2 px-4 pb-3 pt-1">
                {['Orthopedic', 'Needle Holders', 'OEM Manufacturing', 'ISO 13485', 'Dental'].map((t) => (
                  <Link key={t} to="/products" onClick={() => setSearchOpen(false)} className="rounded-full bg-mt-bg px-3 py-1.5 text-sm text-mt-muted transition-colors hover:bg-mt-blue hover:text-white">{t}</Link>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] bg-white lg:hidden" data-testid="mobile-menu">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <img src="/logo-color.png" alt="MEDTREND" className="h-7 w-auto" />
              <button data-testid="mobile-menu-close" onClick={() => setMobileOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-mt-blue/10"><X className="h-6 w-6" strokeWidth={1.5} /></button>
            </div>
            <nav className="flex flex-col px-5 py-6">
              {MENU_ITEMS.map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className="block border-b border-slate-100 py-4 text-left font-display text-xl font-semibold text-mt-ink"
                  >
                    {t(item.label)}
                  </Link>
                </motion.div>
              ))}
              <button onClick={() => { setMobileOpen(false); openQuote(); }} className="mt-8 rounded-full bg-mt-orange px-8 py-4 font-display font-semibold text-white">Get Quote</button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
